import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormSpy } from 'react-final-form';
import type { UpdateCmsPageRequest, WCmsPageView } from '@zola/svc-web-api-ts-client';
// zola-ui
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import FinalFormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
// Hooks
import useSaveChangesPrompt from 'helpers/hooks/useSaveChangesPrompt';
// Redux
import { updatePage } from 'actions/website/PageActions';
import { updatePreview } from 'actions/website/WebsitePreviewActions';
import type { WebsitePreviewReducerState } from 'reducers/website/websitePreview';
import { getPageHome } from 'selectors/website/pageSelectors';
// Utils
import * as Validations from 'components/common/form/commonValidations';
// Styles
import styles from './HomeSettingsForm.module.less';

const HomeSettingsForm = (): JSX.Element => {
  const [isFormDirty, setIsFormDirty] = useState(false);

  useSaveChangesPrompt(isFormDirty);

  const initialValues: WCmsPageView = useSelector(getPageHome);
  const dispatch = useDispatch();
  const onUpdatePage = (pageView: Omit<UpdateCmsPageRequest, 'wedding_account_id'>) =>
    dispatch(updatePage(pageView));
  const onUpdatePreview = (preview: Partial<WebsitePreviewReducerState>) =>
    dispatch(updatePreview(preview));

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>Home Page Settings</h4>
      <Form
        initialValues={initialValues}
        onSubmit={(values: WCmsPageView) => {
          const {
            id,
            title,
            nav_title,
            description,
            hidden,
            header_image_id,
            footer_image_id,
          } = values;
          if (id && typeof hidden !== 'undefined') {
            const payload = {
              id,
              title,
              nav_title,
              description,
              hidden,
              header_image_id,
              footer_image_id,
            };
            onUpdatePage(payload);
          }
        }}
      >
        {({ handleSubmit, pristine, submitting, valid }) => (
          <form onSubmit={handleSubmit} noValidate className="home-page-form home-page-form-v2">
            <div>
              <FinalFormInput
                name="nav_title"
                label="Page title"
                id="cms-page-title"
                maxChars={40}
                validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
                isRequired
              />
              <FinalFormInput
                name="title"
                label="Welcome message"
                maxChars={150}
                id="homepage-welcome-message"
                validate={Validations.MAX_LENGTH_150}
                className={styles.inputSpacing}
              />
              <ButtonV3
                disabled={pristine || !valid || submitting}
                type="submit"
                className={styles.submitButtonSpacing}
              >
                Save Page Settings
              </ButtonV3>
            </div>
            <FormSpy
              subscription={{ values: true, dirty: true }}
              onChange={({ values, dirty }) => {
                setIsFormDirty(dirty);
                const { title, nav_title } = values;
                onUpdatePreview({ title, nav_title });
              }}
            />
          </form>
        )}
      </Form>
    </div>
  );
};

export default HomeSettingsForm;
